import { ORIGIN } from "../utils/configs";
import { routeJavaCommand, waitForLegacyCommand } from "../commands/commandTransport";

export class UserApiError extends Error {
  constructor(code, status, message) { super(message || code); this.name = "UserApiError"; this.code = code; this.status = status; }
}

let activeCsrfToken = null;
export function setActiveUserCsrfToken(value) { activeCsrfToken = value || null; }
export async function submitUserCommand(command) {
  try {
    if (!activeCsrfToken) {
      throw new UserApiError("AUTHENTICATION_REQUIRED", 401, "Sign in from Profile to send a command.");
    }
    if (command.type === "TRADE_COMMAND" && /^\s*\/?codexconf\b/i.test(command.payload?.command || "")) {
      const { adminApi } = await import("../admin/adminApi");
      return await adminApi.submitCommand(command);
    }
    return await routeJavaCommand(command, { legacy: async body => {
    const result = await userRequest("/insights/account/commands", { method: "POST", body, csrfToken: activeCsrfToken });
    return waitForLegacyCommand({ ...result, commandId: body.commandId },
      () => userRequest(`/insights/account/commands/${encodeURIComponent(body.commandId)}`), body.expiresAt);
    } });
  } catch (error) {
    window.dispatchEvent(new CustomEvent("cryptember-command-result", { detail: { accepted: false,
      commandId: command.commandId, type: command.type, message: error?.message || "The command could not be sent. Check the connection." } }));
    throw error;
  }
}

export async function userRequest(path, { method = "GET", body, csrfToken, signal } = {}) {
  const requestSessionToken = activeCsrfToken;
  const response = await fetch(`${ORIGIN}${path}`, {
    method,
    credentials: "same-origin",
    cache: "no-store",
    signal,
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await response.text();
  if (text.length > 3 * 1024 * 1024) throw new UserApiError("RESPONSE_TOO_LARGE", 502);
  let payload = {};
  if (text) { try { payload = JSON.parse(text); } catch (_) { throw new UserApiError("INVALID_RESPONSE", 502); } }
  if (!response.ok) {
    if (response.status === 401 && path.startsWith("/insights/account/") && requestSessionToken === activeCsrfToken) window.dispatchEvent(new Event("cryptember-auth-expired"));
    throw new UserApiError(payload.error || "REQUEST_FAILED", response.status);
  }
  return payload;
}

const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

export async function completeTelegramLogin(mode, fields) {
  const attempt = await userRequest("/insights/auth/telegram", { method: "POST", body: { mode, fields } });
  let delay = 350;
  while (Date.now() < attempt.expiresAt) {
    await wait(delay);
    const result = await userRequest(`/insights/auth/telegram/${encodeURIComponent(attempt.attemptId)}`);
    if (result.status !== "PENDING") return result;
    delay = Math.min(2_500, Math.round(delay * 1.55));
  }
  throw new UserApiError("IDENTITY_SERVICE_UNAVAILABLE", 503);
}

export async function loadTelegramProfile(csrfToken) {
  const result = await routeJavaCommand({ type: "IDENTITY_LOOKUP", payload: {} }, { legacy: async () => ({ profile: await loadLegacyTelegramProfile(csrfToken) }) });
  return result.acknowledgement?.result?.profile || result.profile || null;
}

async function loadLegacyTelegramProfile(csrfToken) {
  const lookup = await userRequest("/insights/account/telegram-profile-lookups", { method: "POST", body: {}, csrfToken });
  let delay = 350;
  while (Date.now() < lookup.expiresAt) {
    await wait(delay);
    const result = await userRequest(`/insights/account/telegram-profile-lookups/${encodeURIComponent(lookup.lookupId)}`);
    if (result.status !== "PENDING") return result.profile || null;
    delay = Math.min(2_500, Math.round(delay * 1.55));
  }
  return null;
}
